/**
 * Veraciter Central - client for /api/central. Wire types mirror
 * packages/bff/src/shell/centralModel.ts (the BFF cannot share a module with
 * the shell bundle, so keep the two in step).
 */

import { useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffFetch } from '@shared/api/bffFetch';

export interface Person {
  oid: string;
  name: string;
}

export interface ShoutOutValue {
  id: string;
  label: string;
  sortOrder: number;
  active: boolean;
}

export type ShoutOutStatus = 'pending' | 'approved' | 'rejected' | 'removed';

export interface ShoutOut {
  id: string;
  sender: Person;
  recipients: Person[];
  /** Null only on shout outs given before titles (backfilled 10/02, then required). */
  title: string | null;
  message: string;
  valueId: string;
  valueLabel: string;
  status: ShoutOutStatus;
  createdAt: string;
  publishedAt: string | null;
  heartCount: number;
  heartedByMe: boolean;
  commentCount: number;
  reviewNote?: string | null;
  reviewedByName?: string | null;
}

export interface ShoutOutComment {
  id: string;
  author: Person;
  body: string;
  createdAt: string;
  mine: boolean;
}

export type PollChoice = 'a' | 'b';
export interface PollResults {
  a: number;
  b: number;
  total: number;
}

export interface Poll {
  id: string;
  /** Custom wording, or null: show "A or B?". */
  question: string | null;
  category: string;
  optionA: string;
  optionB: string;
  opensAt: string;
  closesAt: string;
  myVote: PollChoice | null;
  results: PollResults | null;
}

export interface PollAdminRow extends Poll {
  createdByName: string;
  state: 'scheduled' | 'live' | 'closed';
}

// --- Spotlight (mirrors packages/bff/src/shell/spotlightModel.ts) -----------

/** on_share (Sasha 10/14): hidden from everyone but admins until an admin shares them. */
export type ResultsMode = 'after_close' | 'after_vote' | 'on_share';

export interface SpotlightOption {
  id: string;
  label: string;
}

export interface SpotlightResultRow {
  /** An option id, or `write-in:<key>` for a group of identical write-ins. */
  key: string;
  label: string;
  votes: number;
  writeIn: boolean;
  /** Admin view only. */
  hidden?: boolean;
}

export interface SpotlightPoll {
  question: string;
  options: SpotlightOption[];
  resultsMode: ResultsMode;
  allowWriteIn: boolean;
  closesAt: string;
  closed: boolean;
  myVote: { optionId: string } | { writeIn: string } | null;
  /** Null while hidden from the caller. */
  results: { rows: SpotlightResultRow[]; total: number } | null;
  voteCount: number;
  /** on_share only: when an admin shared the results, else null. */
  resultsSharedAt: string | null;
}

export interface Spotlight {
  id: string;
  title: string;
  body: string;
  imageItemId: string | null;
  imageAlt: string;
  videoItemId: string | null;
  videoUrl: string | null;
  startsAt: string;
  endsAt: string;
  poll: SpotlightPoll | null;
}

export interface SpotlightAdminRow extends Spotlight {
  state: 'scheduled' | 'live' | 'ended';
  /** Never on the home page and closed to votes, whatever the dates. */
  draft: boolean;
  createdByName: string;
}

/** What the admin form sends (create and edit). */
export interface SpotlightInput {
  title: string;
  body: string;
  imageItemId: string | null;
  imageAlt: string;
  videoItemId: string | null;
  videoUrl: string | null;
  startsAt: string;
  endsAt: string;
  draft: boolean;
  poll: {
    question: string;
    /** Keep `id` on existing options so their votes survive an edit. */
    options: { id?: string; label: string }[];
    resultsMode: ResultsMode;
    allowWriteIn: boolean;
    closesAt: string | null;
  } | null;
}

/** Spotlight images and video live in the Academy Assets library, served to any session. */
export const spotlightImageUrl = (itemId: string) => `/api/academy/assets/${encodeURIComponent(itemId)}`;
export const spotlightVideoUrl = (itemId: string) => `/api/academy/assets/${encodeURIComponent(itemId)}/stream`;

// --- This or That analytics -------------------------------------------------

export interface PollAnalyticsRow {
  id: string;
  question: string | null;
  category: string;
  optionA: string;
  optionB: string;
  opensAt: string;
  closesAt: string;
  seasonal: boolean;
  /** Still running. Its a/b are 0 unless the caller voted (or is an admin). */
  live: boolean;
  a: number;
  b: number;
  myVote: PollChoice | null;
}

export interface PollAnalytics {
  polls: PollAnalyticsRow[];
  /** Distinct people who have ever voted. */
  voters: number;
  /** Distinct voters per calendar year (Eastern, by the poll's week). */
  votersByYear: { year: number; voters: number }[];
}

// --- Central admin: engagement and analytics (mirrors centralModel.ts) ------

/** Who engaged with one shout out. Hearts carry only an oid: resolve names from the Vera directory. */
export interface ShoutOutEngagement {
  hearts: { oid: string; at: string }[];
  comments: { id: string; author: Person; body: string; createdAt: string }[];
}

export interface CentralPersonActivity {
  oid: string;
  /** Newest name snapshot Central holds, or '' (resolve from the Vera directory). */
  name: string;
  given: number;
  received: number;
  heartsGiven: number;
  comments: number;
  pollVotes: number;
  spotlightVotes: number;
}

export interface CentralAnalytics {
  /** Null = all time. */
  year: number | null;
  years: number[];
  shoutOuts: {
    total: number;
    hearts: number;
    comments: number;
    pending: number;
    /** 'YYYY-MM', oldest first. */
    byMonth: { month: string; count: number }[];
    byValue: { label: string; count: number }[];
    /** Top 10 by hearts + comments. */
    top: { id: string; title: string | null; senderName: string; recipients: string; hearts: number; comments: number }[];
  };
  thisOrThat: { polls: number; votes: number; voters: number };
  spotlights: { id: string; title: string; startsAt: string; votes: number }[];
  people: CentralPersonActivity[];
}

export function useShoutOutEngagement(id: string | null) {
  return useQuery({
    queryKey: ['central', 'engagement', id],
    enabled: Boolean(id),
    queryFn: () => call<ShoutOutEngagement>(`/api/central/admin/shout-outs/${id}/engagement`),
  });
}

/** `year` null = all time. */
export function useCentralAnalytics(year: number | null, enabled: boolean) {
  return useQuery({
    queryKey: ['central', 'admin-analytics', year],
    enabled,
    queryFn: () => call<CentralAnalytics>(`/api/central/admin/analytics${year ? `?year=${year}` : ''}`),
  });
}

export interface CentralSummary {
  /** False when the caller is outside the preview or the store is down. */
  enabled: boolean;
  /** The calendar year the counts are for (Eastern). */
  year: number;
  shoutOuts: ShoutOut[];
  shoutOutYearTotal: number;
  mine: ShoutOut[];
  pendingCount: number | null;
  /** Shout Out Admins: the oldest few waiting, to moderate from the panel. */
  pending: ShoutOut[];
  poll: Poll | null;
  /** The live Spotlights, newest start first, capped by the server (MAX_LIVE_SPOTLIGHTS). */
  spotlights: Spotlight[];
  /** The newest locked shout out award, while it is fresh; null otherwise. */
  winner: HomeWinner | null;
  values: ShoutOutValue[];
  /** The balloon dog in the header; null when his store is unavailable. */
  mascot: { name: string; out: boolean } | null;
  isShoutOutAdmin: boolean;
  isPollAdmin: boolean;
  isSpotlightAdmin: boolean;
  isAwardAdmin: boolean;
  /** Holds any Central admin role, even while seeing as a typical user. */
  realAdmin: boolean;
  /** This answer is the typical user's view (the admin asked for it). */
  asUser: boolean;
}

// ---------------------------------------------------------------------------

async function call<T>(url: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const res = await bffFetch(url, {
    method: init?.method ?? 'GET',
    headers: init?.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  return data as T;
}

// --- See as a typical user (Sasha 10/14) ------------------------------------
// An admin can flip Central to the view a person with no Central role gets.
// Per tab (sessionStorage), so it never sticks past the session. The BFF
// honours `?as=user` only by taking rights away.

const VIEW_AS_USER_KEY = 'vera.central.viewAsUser';
let viewAsUser = (() => {
  try {
    return sessionStorage.getItem(VIEW_AS_USER_KEY) === '1';
  } catch {
    return false;
  }
})();
const viewAsUserListeners = new Set<() => void>();

export function setViewAsUser(on: boolean) {
  viewAsUser = on;
  try {
    if (on) sessionStorage.setItem(VIEW_AS_USER_KEY, '1');
    else sessionStorage.removeItem(VIEW_AS_USER_KEY);
  } catch {
    // Storage blocked: the toggle still works until the tab reloads.
  }
  viewAsUserListeners.forEach((l) => l());
}

export function useViewAsUser(): boolean {
  return useSyncExternalStore(
    (l) => {
      viewAsUserListeners.add(l);
      return () => viewAsUserListeners.delete(l);
    },
    () => viewAsUser
  );
}

const asUserQuery = (on: boolean) => (on ? '?as=user' : '');

export const centralKeys = {
  root: ['central'] as const,
  summary: ['central', 'summary'] as const,
  feed: (valueId: string | null) => ['central', 'feed', valueId] as const,
  comments: (id: string) => ['central', 'comments', id] as const,
  queue: (status: ShoutOutStatus) => ['central', 'queue', status] as const,
  values: ['central', 'values'] as const,
  polls: ['central', 'polls'] as const,
  spotlights: ['central', 'spotlights'] as const,
  analytics: ['central', 'analytics'] as const,
};

export function useCentralSummary() {
  const asUser = useViewAsUser();
  return useQuery({
    queryKey: [...centralKeys.summary, asUser],
    staleTime: 60_000,
    retry: 1,
    queryFn: () => call<CentralSummary>(`/api/central/summary${asUserQuery(asUser)}`),
  });
}

export function useShoutOutFeed(valueId: string | null, year: number | null, limit = 50) {
  return useQuery({
    queryKey: [...centralKeys.feed(valueId), year],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(limit) });
      if (valueId) q.set('valueId', valueId);
      if (year) q.set('year', String(year));
      return call<{ items: ShoutOut[]; total: number; years: number[] }>(`/api/central/shout-outs?${q}`);
    },
  });
}

/** One published shout out, for /central/shout-outs?id=. */
export function useShoutOut(id: string | null) {
  return useQuery({
    queryKey: ['central', 'shout-out', id],
    enabled: Boolean(id),
    queryFn: () => call<{ shoutOut: ShoutOut }>(`/api/central/shout-outs/${id}`).then((r) => r.shoutOut),
  });
}

export function pollQuestion(p: { question: string | null; optionA: string; optionB: string }): string {
  return p.question ?? `${p.optionA} or ${p.optionB}?`;
}

export function useComments(shoutOutId: string, enabled: boolean) {
  const asUser = useViewAsUser();
  return useQuery({
    queryKey: [...centralKeys.comments(shoutOutId), asUser],
    enabled,
    queryFn: () =>
      call<{ comments: ShoutOutComment[] }>(`/api/central/shout-outs/${shoutOutId}/comments${asUserQuery(asUser)}`).then(
        (r) => r.comments
      ),
  });
}

/** Everything that changes Central refreshes all of Central: it is a few small queries. */
function useCentralMutation<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: centralKeys.root }),
  });
}

export function useCreateShoutOut() {
  return useCentralMutation((v: { recipients: Person[]; title: string; message: string; valueId: string }) =>
    call<{ id: string }>('/api/central/shout-outs', { method: 'POST', body: v })
  );
}

export function useSetHeart() {
  return useCentralMutation((v: { id: string; on: boolean }) =>
    call(`/api/central/shout-outs/${v.id}/heart`, { method: 'POST', body: { on: v.on } })
  );
}

export function useAddComment() {
  return useCentralMutation((v: { id: string; body: string }) =>
    call(`/api/central/shout-outs/${v.id}/comments`, { method: 'POST', body: { body: v.body } })
  );
}

export function useVote() {
  return useCentralMutation((v: { pollId: string; choice: PollChoice }) =>
    call<{ myVote: PollChoice; results: PollResults }>(`/api/central/polls/${v.pollId}/vote`, {
      method: 'POST',
      body: { choice: v.choice },
    })
  );
}

export function useSpotlightVote() {
  return useCentralMutation((v: { id: string; optionId?: string; writeIn?: string }) =>
    call<{ spotlight: Spotlight }>(`/api/central/spotlights/${v.id}/vote`, {
      method: 'POST',
      body: v.optionId ? { optionId: v.optionId } : { writeIn: v.writeIn },
    })
  );
}

export function usePollAnalytics() {
  const asUser = useViewAsUser();
  return useQuery({
    queryKey: [...centralKeys.analytics, asUser],
    staleTime: 5 * 60_000,
    queryFn: () => call<PollAnalytics>(`/api/central/this-or-that/analytics${asUserQuery(asUser)}`),
  });
}

// --- Spotlight Admin -------------------------------------------------------

export function useAdminSpotlights(enabled: boolean) {
  return useQuery({
    queryKey: centralKeys.spotlights,
    enabled,
    queryFn: () => call<{ items: SpotlightAdminRow[] }>('/api/central/admin/spotlights').then((r) => r.items),
  });
}

/** Any spotlight as voters would see it, for the home page preview (Spotlight Admins). */
/** Share (or take back) an on_share poll's results; endsAt keeps the spotlight up longer. */
export function useShareSpotlightResults() {
  return useCentralMutation((v: { id: string; shared: boolean; endsAt?: string | null }) =>
    call(`/api/central/admin/spotlights/${v.id}/share-results`, {
      method: 'POST',
      body: { shared: v.shared, endsAt: v.endsAt ?? null },
    })
  );
}

export function useSpotlightPreview(id: string | null) {
  return useQuery({
    queryKey: ['central', 'spotlight-preview', id],
    enabled: Boolean(id),
    queryFn: () => call<{ spotlight: Spotlight }>(`/api/central/admin/spotlights/${id}/preview`).then((r) => r.spotlight),
  });
}

/** Create (no id) or edit (with id). */
export function useSaveSpotlight() {
  return useCentralMutation((v: SpotlightInput & { id?: string }) => {
    const { id, ...body } = v;
    return id
      ? call(`/api/central/admin/spotlights/${id}`, { method: 'PUT', body })
      : call<{ id: string }>('/api/central/admin/spotlights', { method: 'POST', body });
  });
}

export function useDeleteSpotlight() {
  return useCentralMutation((id: string) => call(`/api/central/admin/spotlights/${id}`, { method: 'DELETE' }));
}

/** Hide a write-in group from public results (or show it again). `key` is the row key minus `write-in:`. */
export function useHideWriteIn() {
  return useCentralMutation((v: { id: string; key: string; hidden: boolean }) =>
    call(`/api/central/admin/spotlights/${v.id}/write-ins`, { method: 'POST', body: { key: v.key, hidden: v.hidden } })
  );
}

export interface UploadedAsset {
  itemId: string;
  name: string;
  size: number;
  contentType: string;
  url: string;
}

async function readUpload<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? fallback);
  return data as T;
}

export async function uploadSpotlightImage(file: File): Promise<UploadedAsset> {
  const form = new FormData();
  form.append('file', file);
  const res = await bffFetch('/api/central/admin/spotlight-assets', { method: 'POST', body: form });
  return (await readUpload<{ asset: UploadedAsset }>(res, 'Upload failed')).asset;
}

/**
 * Upload a video in chunks (the Academy pattern: the BFF holds a Graph upload
 * session and the chunks must arrive in order, so this loop stays serial).
 */
export async function uploadSpotlightVideo(file: File, onProgress?: (fraction: number) => void): Promise<UploadedAsset> {
  const start = await bffFetch('/api/central/admin/spotlight-assets/video/session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, contentType: file.type, size: file.size }),
  });
  const { uploadId, chunkBytes } = await readUpload<{ uploadId: string; chunkBytes: number }>(
    start,
    'Could not start the upload'
  );
  onProgress?.(0);
  for (let offset = 0; offset < file.size; offset += chunkBytes) {
    const res = await bffFetch(`/api/central/admin/spotlight-assets/video/session/${uploadId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file.slice(offset, Math.min(offset + chunkBytes, file.size)),
    });
    const result = await readUpload<{ asset: UploadedAsset | null; received: number }>(res, 'Upload failed');
    onProgress?.(Math.min(result.received / file.size, 1));
    if (result.asset) return result.asset;
  }
  throw new Error('Upload finished without a saved file. Try again.');
}

// --- Shout Out Admin -------------------------------------------------------

export function useSetShoutOutTitle() {
  return useCentralMutation((v: { id: string; title: string }) =>
    call(`/api/central/admin/shout-outs/${v.id}/title`, { method: 'PATCH', body: { title: v.title } })
  );
}

export function useReviewQueue(status: ShoutOutStatus, enabled: boolean) {
  return useQuery({
    queryKey: centralKeys.queue(status),
    enabled,
    queryFn: () =>
      call<{ items: ShoutOut[] }>(`/api/central/admin/shout-outs?status=${status}`).then((r) => r.items),
  });
}

export function useReview() {
  return useCentralMutation((v: { id: string; decision: 'approve' | 'reject'; note: string }) =>
    call(`/api/central/admin/shout-outs/${v.id}/review`, {
      method: 'POST',
      body: { decision: v.decision, note: v.note },
    })
  );
}

export function useRemoveShoutOut() {
  return useCentralMutation((v: { id: string; note: string }) =>
    call(`/api/central/admin/shout-outs/${v.id}/remove`, { method: 'POST', body: { note: v.note } })
  );
}

export function useDeleteComment() {
  return useCentralMutation((id: string) => call(`/api/central/admin/comments/${id}`, { method: 'DELETE' }));
}

export function useAdminValues(enabled: boolean) {
  return useQuery({
    queryKey: centralKeys.values,
    enabled,
    queryFn: () => call<{ values: ShoutOutValue[] }>('/api/central/admin/values').then((r) => r.values),
  });
}

export function useAddValue() {
  return useCentralMutation((label: string) =>
    call('/api/central/admin/values', { method: 'POST', body: { label } })
  );
}

export function useUpdateValue() {
  return useCentralMutation((v: { id: string; label?: string; active?: boolean; sortOrder?: number }) =>
    call(`/api/central/admin/values/${v.id}`, {
      method: 'PATCH',
      body: { label: v.label, active: v.active, sortOrder: v.sortOrder },
    })
  );
}

// --- This or That Admin ----------------------------------------------------

export interface PollSettings {
  flipWeekday: number;
  flipHour: number;
  flipMinute: number;
  runWeeks: number;
  timeZone: string;
  anchorDate: string;
  updatedAt: string;
  updatedByName: string;
}

export interface BankEntry {
  id: string;
  category: string;
  optionA: string;
  optionB: string;
  question: string | null;
  position: number;
  pinnedOn: string | null;
  windowFrom: string | null;
  windowUntil: string | null;
  tags: string[];
  active: boolean;
  timesRun: number;
  lastRunAt: string | null;
  nextRunAt: string | null;
  conflict: boolean;
  pinExpired: boolean;
}

export interface UpcomingSlot {
  startsAt: string;
  endsAt: string;
  itemId: string | null;
  recycled: boolean;
  live: boolean;
  /** Paused: no poll that week, no question used up. */
  paused: boolean;
}

export interface PollPlan {
  settings: PollSettings;
  items: BankEntry[];
  upcoming: UpcomingSlot[];
  recycleStartsAt: string | null;
  /** Paused weeks from now on, as slot start dates (YYYY-MM-DD). */
  pauses: string[];
  history: PollAdminRow[];
}

export interface BankEntryInput {
  category?: string;
  optionA?: string;
  optionB?: string;
  question?: string | null;
  pinnedOn?: string | null;
  windowFrom?: string | null;
  windowUntil?: string | null;
  active?: boolean;
}

const pollKeys = { plan: ['central', 'this-or-that'] as const };

export function usePollPlan(enabled: boolean) {
  return useQuery({
    queryKey: pollKeys.plan,
    enabled,
    queryFn: () => call<PollPlan>('/api/central/admin/this-or-that'),
  });
}

export function useSavePollSettings() {
  return useCentralMutation((v: { flipWeekday: number; flipHour: number; flipMinute: number; runWeeks: number }) =>
    call('/api/central/admin/this-or-that/settings', { method: 'PUT', body: v })
  );
}

export function useAddBankEntry() {
  return useCentralMutation((v: BankEntryInput) =>
    call('/api/central/admin/this-or-that/bank', { method: 'POST', body: v })
  );
}

export function useUpdateBankEntry() {
  return useCentralMutation((v: BankEntryInput & { id: string }) => {
    const { id, ...body } = v;
    return call(`/api/central/admin/this-or-that/bank/${id}`, { method: 'PATCH', body });
  });
}

export function useRemoveBankEntry() {
  return useCentralMutation((id: string) =>
    call<{ outcome: 'deleted' | 'deactivated' }>(`/api/central/admin/this-or-that/bank/${id}`, { method: 'DELETE' })
  );
}

export function useReorderBank() {
  return useCentralMutation((ids: string[]) =>
    call('/api/central/admin/this-or-that/reorder', { method: 'POST', body: { ids } })
  );
}

export function useImportBank() {
  return useCentralMutation((v: { csv: string; interleave: boolean }) =>
    call<{ added: number; duplicates: string[]; problems: string[] }>('/api/central/admin/this-or-that/import', {
      method: 'POST',
      body: v,
    })
  );
}

/** Pause the week containing `weekOf` (YYYY-MM-DD): no poll, no question used up. */
export function usePauseWeek() {
  return useCentralMutation((weekOf: string) =>
    call<{ weekOf: string }>('/api/central/admin/this-or-that/pauses', { method: 'POST', body: { weekOf } })
  );
}

export function useUnpauseWeek() {
  return useCentralMutation((weekOf: string) =>
    call(`/api/central/admin/this-or-that/pauses/${weekOf}`, { method: 'DELETE' })
  );
}

/** Skip this week: delete the live poll; the slot stays empty until the next flip. */
export function useDeletePoll() {
  return useCentralMutation((id: string) => call(`/api/central/admin/polls/${id}`, { method: 'DELETE' }));
}

// --- The weekly shout out award (mirrors packages/bff/src/shell/awardModel.ts) ---

export type AwardPool = 'received' | 'given';
export type EntryRule = 'per_shout_out' | 'per_person';
export type DiscardReason = 'just_won' | 'not_here' | 'other';
export type AwardStatus = 'drawn' | 'locked' | 'discarded';

export interface AwardConfig {
  amountCents: number;
  pool: AwardPool;
  entryRule: EntryRule;
  excludeWinnerDays: number;
  windowDays: number;
  updatedByName: string;
  updatedAt: string;
}

export interface Award {
  id: string;
  drawnAt: string;
  drawnBy: Person;
  windowStart: string;
  windowEnd: string;
  pool: AwardPool;
  entryRule: EntryRule;
  entryCount: number;
  candidateCount: number;
  winner: Person;
  shoutOutId: string | null;
  shoutOutTitle: string | null;
  shoutOutSenderName: string | null;
  amountCents: number;
  status: AwardStatus;
  discardReason: DiscardReason | null;
  discardNote: string | null;
  lockedAt: string | null;
  lockedBy: Person | null;
  note: string | null;
  /** An Award Admin took this winner off the home tile early. */
  homeTakenDown: { at: string; byName: string } | null;
}

/** The winner tile on the home panel (Sasha 10/20); no amount. */
export interface HomeWinner {
  winner: Person;
  windowStart: string;
  windowEnd: string;
  pool: AwardPool;
  lockedAt: string;
  shoutOutId: string | null;
  shoutOutTitle: string | null;
  /** The whole message, for the hover. */
  shoutOutMessage: string | null;
  shoutOutSenderName: string | null;
}

export interface AwardsPage {
  config: AwardConfig;
  open: Award | null;
  history: Award[];
  sittingOut: { oid: string; name: string; lockedAt: string }[];
  /** The locked award the home tile is for right now, taken down or not. */
  homeAwardId: string | null;
}

const awardKeys = { page: ['central', 'awards'] as const };

export function useAwards(enabled: boolean) {
  return useQuery({ queryKey: awardKeys.page, enabled, queryFn: () => call<AwardsPage>('/api/central/admin/awards') });
}

export function useDrawAward() {
  return useCentralMutation((v: { windowStart: string; windowEnd: string; pool: AwardPool; entryRule: EntryRule }) =>
    call<{ award: Award }>('/api/central/admin/awards/draw', { method: 'POST', body: v })
  );
}

export function useRedrawAward() {
  return useCentralMutation((v: { id: string; reason: DiscardReason; note: string }) =>
    call<{ award: Award }>(`/api/central/admin/awards/${v.id}/redraw`, {
      method: 'POST',
      body: { reason: v.reason, note: v.note },
    })
  );
}

export function useLockAward() {
  return useCentralMutation((v: { id: string; note: string }) =>
    call<{ award: Award }>(`/api/central/admin/awards/${v.id}/lock`, { method: 'POST', body: { note: v.note } })
  );
}

/** Take a locked winner off the home tile early (down: true), or put them back. */
export function useSetAwardHome() {
  return useCentralMutation((v: { id: string; down: boolean }) =>
    call<{ award: Award }>(`/api/central/admin/awards/${v.id}/home`, { method: 'POST', body: { down: v.down } })
  );
}

export type AwardConfigInput = Partial<Pick<AwardConfig, 'amountCents' | 'pool' | 'entryRule' | 'excludeWinnerDays' | 'windowDays'>>;

export function useSaveAwardConfig() {
  return useCentralMutation((v: AwardConfigInput) =>
    call<{ config: AwardConfig }>('/api/central/admin/awards/config', { method: 'PUT', body: v })
  );
}

// --- The mascot (Sasha 10/16) ------------------------------------------------
// docs/design/home/MASCOT.md. Spotlight Admins set his name and schedule.

export type MascotMode = 'off' | 'always' | 'weekly' | 'monthly' | 'window';
export type MonthlyRule = 'nth_weekday' | 'day_of_month';

export interface MascotSchedule {
  name: string;
  mode: MascotMode;
  /** 0 = Sunday ... 6 = Saturday. */
  weekdays: number[];
  monthlyRule: MonthlyRule;
  /** 1 to 4, or -1 for the last. */
  monthlyNth: number;
  monthlyWeekday: number;
  monthlyDay: number;
  windowStart: string | null;
  windowEnd: string | null;
}

export interface MascotAdmin {
  config: MascotSchedule & { updatedByName: string; updatedAt: string };
  /** Out today, for everyone. */
  out: boolean;
  /** The next day he is out (today included), or null. */
  nextOut: string | null;
}

export function useMascotAdmin(enabled: boolean) {
  return useQuery({
    queryKey: ['central', 'mascot'],
    enabled,
    queryFn: () => call<MascotAdmin>('/api/central/admin/mascot'),
  });
}

export function useSaveMascot() {
  return useCentralMutation((v: MascotSchedule) =>
    call<MascotAdmin>('/api/central/admin/mascot', { method: 'PUT', body: v })
  );
}

/** Opens the mascot in his own window, or brings an open one forward. */
export function openMascot() {
  window.open('/mascot.html', 'vera-mascot', 'popup,width=1200,height=800')?.focus();
}

/** Every engagement in one workbook; any Central admin. A plain link: the BFF streams the file. */
export const CENTRAL_EXPORT_URL = '/api/central/admin/export.xlsx';

// --- One person's shout outs (the analytics drill) -------------------------

export interface PersonShoutOut {
  id: string;
  title: string | null;
  message: string;
  valueLabel: string;
  status: ShoutOutStatus;
  createdAt: string;
  publishedAt: string | null;
  senderName: string;
  recipientNames: string;
  hearts: number;
  comments: number;
}

export function usePersonShoutOuts(oid: string | null) {
  return useQuery({
    queryKey: ['central', 'person-shout-outs', oid],
    enabled: Boolean(oid),
    queryFn: () =>
      call<{ given: PersonShoutOut[]; received: PersonShoutOut[] }>(
        `/api/central/admin/people/${encodeURIComponent(oid ?? '')}/shout-outs`
      ),
  });
}
